import React, { ReactNode, useEffect } from 'react';
import { ReactComponent as CloseIcon } from './icons/x-bold.svg';

interface SidebarProps
	{
	isOpen: boolean;
	onClose: () => void;
	title: string;
	children: ReactNode;
	}

// Generic slide-in/out panel from the right -- content-agnostic, just a
// title, a close button, and a contents slot. Stays mounted (rather than
// unmounting when closed) so the open/close transition can animate; visual
// state is driven entirely by the `is-open` class in sidebar.scss.
export const Sidebar = ({ isOpen, onClose, title, children }: SidebarProps) =>
	{
	useEffect(() =>
		{
		if (!isOpen)
			{
			return;
			}

		const handleKeydown = (event: KeyboardEvent) =>
			{
			if (event.key === 'Escape')
				{
				onClose();
				}
			};

		document.addEventListener('keydown', handleKeydown);
		document.body.classList.add('ocean-sidebar-open');

		return () =>
			{
			document.removeEventListener('keydown', handleKeydown);
			document.body.classList.remove('ocean-sidebar-open');
			};
		}, [isOpen, onClose]);

	return (
		<>
		<div
			className={`ocean-sidebar__overlay ${isOpen ? 'is-open' : ''}`}
			onClick={onClose}
			aria-hidden="true"
		/>
		<aside
			className={`ocean-sidebar ${isOpen ? 'is-open' : ''}`}
			role="dialog"
			aria-modal="true"
			aria-label={title}
			aria-hidden={!isOpen}
		>
			<div className="ocean-sidebar__header">
				<p className="ocean-sidebar__title">{title}</p>
				<button type="button" className="ocean-sidebar__close" onClick={onClose} aria-label="Close">
					<CloseIcon className="ocean-sidebar__close-icon" />
				</button>
			</div>
			<div className="ocean-sidebar__content">
				{children}
			</div>
		</aside>
		</>
		);
	};
